import { io, type Socket } from "socket.io-client";

/**
 * One Socket.IO connection for the whole tab, reference counted.
 *
 * The Play page used to open its own connection and hand it to the countdown
 * hook; now that the scoreboard and the admin dashboard listen too, each of
 * them opening a socket would mean three connections per tab.
 */
let socket: Socket | null = null;
let refs = 0;
let teardown: number | null = null;

export function acquireSocket(): Socket {
  /* A pending teardown means something is re-acquiring within the grace
     period: keep the existing connection. */
  if (teardown !== null) {
    clearTimeout(teardown);
    teardown = null;
  }
  if (!socket) {
    socket = io(import.meta.env.VITE_SOCKET_BASE ?? window.location.origin, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      withCredentials: false,
    });
  }
  refs += 1;
  return socket;
}

export function releaseSocket(): void {
  refs = Math.max(0, refs - 1);
  if (refs > 0 || !socket) return;

  /* Closing the moment the count hits zero churns the connection: React's
     StrictMode remounts every effect, and navigating between pages briefly
     drops to zero between unmount and mount. Wait a beat so those cases
     reuse the socket instead of reconnecting. */
  if (teardown !== null) clearTimeout(teardown);
  teardown = window.setTimeout(() => {
    teardown = null;
    if (refs === 0 && socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
  }, 1500);
}

/** Events the server pushes. Mirrors backend/src/ws/events.ts. */
export const SOCKET_EVENTS = {
  scoreboardUpdated: "scoreboard:updated",
  /** Roster change: a player joined, teams generated, a team renamed. */
  teamsUpdated: "teams:updated",
  gameStarted: "game:started",
  gameEnded: "game:ended",
  gameTimeAdjusted: "game:time_adjusted",
  teamRenamed: "team:renamed",
} as const;
